import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { BackButton } from '../../../components/molecules/Buttons/BackButton';
import { UPDATE_CLASSE_ONG } from '../../../graphql/mutation/classe/updateClasseOng.mutation';
import { useMutation } from '@apollo/client';
import toast from 'react-hot-toast';
import { UPDATE_USER_ONG } from '../../../graphql/mutation/user/updateUserOng.mutation';
import { ButtonWithText } from '../../../components/atoms/Buttons/ButtonWithText';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../../store/reducers';
import { QUERY_USER_HOME } from '../../../graphql/query/user/queryUserHome';
import useStyle from './ngoChoice.style';
import useGoToPage from '../../../hooks/useGoToPage';
import pages from '../../../routes/pages';
import { NgoListWithInfos } from '../../../domain/ngo/ngoDescription';
import { HeaderText } from '../../../components/atoms/Texts/HeaderText';
import emoji from '../../../theme/emoji';
import { PercentPicker } from '../../../components/atoms/Inputs/PercentPicker';
import { FeatureContainer } from '../../../components/molecules/Containers/FeatureContainer';
import { ContainerTwoThirds } from '../../../components/layouts/ContainerTwoThirds';
import { getRecapSentence } from '../../../domain/ngo/ngo';
import { NgoCard } from '../../../components/molecules/NgoCard';
import { QUERY_CLASSE_WITH_NOTE_AND_STUDENTS_ORDERS } from '../../../graphql/query/classe/queryClasseWithNoteAndStudentsOrders';
import { MY_ID } from '../../../helpers/user/authorization';
import { updateUser } from 'store/actions/user.actions';

export const NgoChoice = () => {
  const { type } = useParams<{ type: string }>();
  const { user } = useSelector((state: AppState) => state);
  const [selectedNgo, selectNgo] = useState<any>({});
  const [percentOfComissionForNgo, setPercentOfComission] = useState(0);
  const dispatch = useDispatch();
  const classes = useStyle();
  const goToNgoPage = useGoToPage(pages.NGO.path);
  const [updateClassNgo] = useMutation(UPDATE_CLASSE_ONG, {
    refetchQueries: [
      {
        query: QUERY_CLASSE_WITH_NOTE_AND_STUDENTS_ORDERS,
        variables: {
          userId: MY_ID
        }
      }
    ],
    onCompleted: () => {
      toast.success('Mise à jour effectuée', {
        icon: '🎉'
      });
    },
    onError: () => {
      toast.error('Erreur dans la mise à jour', {
        icon: '😕'
      });
    }
  });
  const [updateUserNgo] = useMutation(UPDATE_USER_ONG, {
    refetchQueries: [{ query: QUERY_USER_HOME }],
    onCompleted: data => {
      toast.success('Ong sélectionnée', {
        icon: '🎉'
      });
      dispatch(
        updateUser({ ong: data.updateUsersPermissionsUser.data.attributes.ong })
      );
    },
    onError: () => {
      toast.error("Erreur dans la selection de l'ONG", {
        icon: '😕'
      });
    }
  });

  const updateNgo = async () => {
    if (type === 'user') {
      await updateUserNgo({
        variables: { id: user.id, data: selectedNgo }
      });
    }
    if (type === 'classe') {
      if (selectedNgo.percentDedicatedToOng)
        setPercentOfComission(selectedNgo.percentDedicatedToOng);
      await updateClassNgo({
        variables: { id: user.classe.id, data: selectedNgo }
      });
    }
    setTimeout(() => {
      goToNgoPage();
    }, 1500);
  };

  const handleClickNgo = name => {
    selectNgo({ ...selectedNgo, ong: name });
  };

  const cancelOngParticipation = async () => {
    await updateClassNgo({
      variables: {
        id: user.classe.id,
        data: { ong: 'Aucune_ONG', percentDedicatedToOng: 0 }
      }
    });
    setTimeout(() => {
      goToNgoPage();
    }, 1500);
  };

  return (
    <div className={classes.root}>
      <BackButton text="Revenir aux chiffres" />
      <FeatureContainer withPadding={true}>
        <h3>
          Comment ça fonctionne ?{' '}
          <span role="img" aria-label="emoji">
            🤔
          </span>
        </h3>
        {type === 'classe' && (
          <>
            <p>
              En tant que professeur(e), vous organisez une discussion avec
              votre classe pour décider si vous souhaitez reverser une partie de
              votre commission à une ONG partenaire ou non. Ce choix peut être
              effectué au cours du challenge, et vous avez jusqu'au 30 mars
              inclus pour prendre votre décision. Si aucun choix n'est fait, 5 %
              seront reversés aléatoirement à une ONG.
            </p>
            <ul className={classes.stepsList}>
              <li>
                1 - Commencez par choisir l'ONG à laquelle vous souhaitez
                contribuer collectivement, ou cliquez sur "Je ne souhaite pas
                aider une ONG".
              </li>
              <li>
                2 - Si vous décidez, avec votre classe, de soutenir une ONG,
                choisissez le pourcentage de votre commission à reverser.
              </li>
              <li>3 - N'oubliez pas de valider vos modifications.</li>
            </ul>
          </>
        )}
        {type === 'user' && (
          <>
            <p>
              Vos clients peuvent réaliser un don d'1 € lors de leur achat pour
              l'association que vous souhaitez personnellement soutenir.
            </p>
            <ul className={classes.stepsList}>
              <li>
                1 - Choisissez l'ONG qui recevra les "dons 1 €" de vos clients
              </li>
              <li>2 - Validez votre choix grâce au bouton en bas de page</li>
            </ul>
          </>
        )}
      </FeatureContainer>
      <h1 className={classes.headerText}>Choisissez une ONG</h1>
      <div className={classes.ngoListWrapper}>
        {NgoListWithInfos && NgoListWithInfos.length > 0 ? (
          NgoListWithInfos.map((ong, index) => (
            <NgoCard
              key={index}
              ngo={ong}
              handleClickNgo={handleClickNgo}
              selectedNgo={selectedNgo}
            />
          ))
        ) : (
          <p>
            Erreur lors de l'affichage des ONG, merci de contacter France
            Challenges en précisant l'ONG que vous souhaitez choisir entre Un
            enfant par la main, Société Protectrice des Animaux et Planète
            Urgence
          </p>
        )}
      </div>
      {type === 'classe' && (
        <ContainerTwoThirds
          variant="reverse"
          col1={
            <div className={classes.root}>
              <HeaderText
                text="Pourcentage reversé à l'ONG"
                emoji={emoji['']}
              />
              <FeatureContainer>
                <PercentPicker
                  defaultValue={percentOfComissionForNgo}
                  handleChange={value =>
                    selectNgo({ ...selectedNgo, percentDedicatedToOng: value })
                  }
                />
              </FeatureContainer>
            </div>
          }
          col2={
            <div className={classes.root}>
              <h1 className={classes.headerText}>Récap</h1>
              <FeatureContainer>
                <p className={classes.recapSentence}>
                  {getRecapSentence(
                    selectedNgo.ong,
                    selectedNgo.percentDedicatedToOng
                  )}
                </p>
              </FeatureContainer>
            </div>
          }
        />
      )}
      <div className={classes.submitButton}>
        <ButtonWithText
          content="Valider les modifications"
          onClick={updateNgo}
          disabled={
            !selectedNgo.ong ||
            (type === 'classe' && !selectedNgo.percentDedicatedToOng)
          }
        />
        {type === 'classe' && (
          <ButtonWithText
            content="Nous ne souhaitons pas contribuer à une ONG"
            onClick={cancelOngParticipation}
            variant="danger"
          />
        )}
      </div>
    </div>
  );
};
